import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { discussionsAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  MessageCircle,
  Plus,
  MessageSquare,
  Heart,
  Clock,
  Send,
} from 'lucide-react';

export const Discussions = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDiscussion, setNewDiscussion] = useState({ title: '', content: '', tags: '' });
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  useEffect(() => {
    fetchDiscussions();
  }, []);

  const fetchDiscussions = async () => {
    try {
      const response = await discussionsAPI.getAll();
      setDiscussions(response.data);
    } catch (error) {
      console.error('Failed to fetch discussions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDiscussion = async (e) => {
    e.preventDefault();
    if (!newDiscussion.title.trim() || !newDiscussion.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setCreating(true);
    try {
      const tags = newDiscussion.tags.split(',').map(t => t.trim()).filter(Boolean);
      const response = await discussionsAPI.create({
        title: newDiscussion.title,
        content: newDiscussion.content,
        tags,
      });
      setDiscussions([response.data, ...discussions]);
      setNewDiscussion({ title: '', content: '', tags: '' });
      setDialogOpen(false);
      toast.success('Discussion created!');
    } catch (error) {
      toast.error('Failed to create discussion');
    } finally {
      setCreating(false);
    }
  };

  const handleViewDiscussion = async (discussion) => {
    setSelectedDiscussion(discussion);
    setLoadingComments(true);
    try {
      const response = await discussionsAPI.getComments(discussion.id);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedDiscussion) return;
    try {
      const response = await discussionsAPI.addComment(selectedDiscussion.id, {
        discussion_id: selectedDiscussion.id,
        content: newComment,
      });
      setComments([...comments, response.data]);
      setNewComment('');
      // Update comment count
      setDiscussions(discussions.map(d =>
        d.id === selectedDiscussion.id
          ? { ...d, comment_count: (d.comment_count || 0) + 1 }
          : d
      ));
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="discussions-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <MessageCircle className="w-8 h-8 text-blue-500" />
              {t('discussions.title', 'Discussions')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('discussions.subtitle', 'Connect with peers and share knowledge')}
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="new-discussion-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('discussions.newDiscussion', 'New Discussion')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('discussions.startDiscussion', 'Start a Discussion')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateDiscussion} className="space-y-4">
                <div>
                  <Input
                    placeholder={t('discussions.titlePlaceholder', 'Discussion title')}
                    value={newDiscussion.title}
                    onChange={(e) => setNewDiscussion({ ...newDiscussion, title: e.target.value })}
                    data-testid="discussion-title-input"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder={t('discussions.contentPlaceholder', 'Share your thoughts, questions, or insights...')}
                    value={newDiscussion.content}
                    onChange={(e) => setNewDiscussion({ ...newDiscussion, content: e.target.value })}
                    rows={4}
                    data-testid="discussion-content-input"
                  />
                </div>
                <div>
                  <Input
                    placeholder={t('discussions.tagsPlaceholder', 'Tags (comma separated)')}
                    value={newDiscussion.tags}
                    onChange={(e) => setNewDiscussion({ ...newDiscussion, tags: e.target.value })}
                    data-testid="discussion-tags-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={creating} data-testid="submit-discussion-btn">
                  {creating ? 'Creating...' : 'Create Discussion'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Discussion List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main List */}
          <div className="lg:col-span-2 space-y-4">
            {discussions.length > 0 ? (
              discussions.map((discussion) => (
                <Card 
                  key={discussion.id} 
                  className="card-hover cursor-pointer"
                  onClick={() => handleViewDiscussion(discussion)}
                  data-testid={`discussion-${discussion.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarImage src={discussion.author_avatar} />
                        <AvatarFallback>{discussion.author_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg hover:text-primary transition-colors">
                          {discussion.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                          {discussion.content}
                        </p>
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            {discussion.comment_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-4 h-4" />
                            {discussion.likes || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(discussion.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {discussion.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {discussion.tags.map(tag => (
                              <Badge key={tag} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="p-12 text-center">
                <MessageCircle className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Discussions Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Be the first to start a discussion!
                </p>
                <Button onClick={() => setDialogOpen(true)}>
                  Start Discussion
                </Button>
              </Card>
            )}
          </div>

          {/* Selected Discussion / Comments */}
          <div className="lg:col-span-1">
            {selectedDiscussion ? (
              <Card className="sticky top-20" data-testid="discussion-detail">
                <CardHeader>
                  <CardTitle className="text-lg">{selectedDiscussion.title}</CardTitle>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={selectedDiscussion.author_avatar} />
                      <AvatarFallback>{selectedDiscussion.author_name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span>{selectedDiscussion.author_name}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm">{selectedDiscussion.content}</p>
                  
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Comments ({comments.length})</h4>
                    
                    {loadingComments ? (
                      <div className="space-y-2">
                        <Skeleton className="h-16" />
                        <Skeleton className="h-16" />
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {comments.map((comment) => (
                          <div key={comment.id} className="p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center gap-2 mb-1">
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={comment.author_avatar} />
                                <AvatarFallback>{comment.author_name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <span className="text-sm font-medium">{comment.author_name}</span>
                            </div>
                            <p className="text-sm">{comment.content}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2 mt-4">
                      <Input
                        placeholder="Add a comment..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                        data-testid="comment-input"
                      />
                      <Button size="icon" onClick={handleAddComment} data-testid="submit-comment-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="p-8 text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Select a discussion to view details
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Discussions;
